import React, {
  useEffect, useRef, useState, useContext,
} from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import { isJSON, getLocalizedImage } from '../../utils';
import type { LocalizedImageMap } from '../../utils';
import { useUserLocations, useHeaderFooter } from '../../hooks';
import { AppDataContext } from '../../context';
import { CarouselCourseCard } from '../../components';

import './index.scss';
import messages from './messages';

import arrowRightImg from '../../img/arrow-right.svg';
import {
  heroImg, pabloPicImg, princeAkachiImg, playImg, learningJourneyImg,
  bannerEn, bannerFr, bannerHi, bannerEs, bannerSw, bannerZh,
} from '../../img/home';

interface HomePageProps {
  children?: React.ReactNode;
}

const HomePage: React.FC<HomePageProps> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const mapIframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const intl = useIntl();
  const { allowedCourses } = useContext(AppDataContext);
  const { allUsersLocations: locations } = useUserLocations();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const mapIframeSrc = `${window.location.origin}/location-map.html`;
  const firstOfAllowedCourseId: string = allowedCourses[0]?.course_id;
  const bannerImages: LocalizedImageMap = {
    en: bannerEn,
    fr: bannerFr,
    hi: bannerHi,
    'es-419': bannerEs,
    sw: bannerSw,
    'zh-cn': bannerZh,
  };
  const bannerSrc = getLocalizedImage(bannerImages);

  const handleCheckIframeReady = (event: MessageEvent) => {
    if (!!event && isJSON(event?.data)) {
      const msg = JSON.parse(event.data);

      if (msg?.ready) {
        mapIframeRef?.current?.contentWindow?.postMessage(JSON.stringify({ locations }), window.location.origin);
      }

      if (msg?.height && mapIframeRef?.current) {
        mapIframeRef.current.style.height = `${msg?.height}px`;
      }
    }
  };

  useEffect(() => {
    if (Object.keys(locations).length === 0) {
      return undefined;
    }

    window.addEventListener('message', handleCheckIframeReady);

    return () => {
      window.removeEventListener('message', handleCheckIframeReady);
    };
  }, [locations]);

  const handlePlay = () => {
    setIsPlaying(true);

    if (videoRef.current) {
      videoRef.current?.play();
    }
  };

  const homeIntroText = (chunks: React.ReactNode) => <span>{chunks}</span>;

  return (
    <div className="container-center-horizontal">
      <div className="homepage-main screen">
        {headerContent}
        <div className="homepage">
          <div className="overlap-group3">
            <div className="hero-image">
              <div className="overlap-group2">
                <div className="full-bleed-image">
                  <div className="overlap-group" />
                </div>
                <div className="hero-image-1">
                  <div className="overlap-group1">
                    <div className="full-bleed-image-1">
                      <div className="unsplash-container">
                        <img
                          className="unsplash-container-item"
                          src={heroImg}
                          alt=""
                        />
                      </div>
                    </div>
                    <img
                      style={{ height: '50vh' }}
                      className="graphic"
                      src={bannerSrc}
                      alt="Graphic"
                    />
                    <div className="rectangle-21 rectangle" />
                    <div className="grad-1 grad-2" />
                    <div className="grad-for-nav" />
                  </div>
                </div>
              </div>
            </div>
            <div className="home-intro-text">
              <p>
                {intl.formatMessage(messages.jointInitiative, {
                  lineBreak: <br />,
                  span: homeIntroText,
                })}
              </p>
            </div>
          </div>
        </div>
        <div className="purple-home">
          <div className="homepageContainer">
            <div className="intro-block intro-block-custom">
              <div className="intro-block-text">
                <p className="be-a-better-leader-build-a-better-world">
                  {intl.formatMessage(messages.beBetterLeader, { lineBreak: <br /> })}
                </p>
                <p className="lorem-ipsum-dolor-si-1 inter-light-congress-blue-22px">
                  {intl.formatMessage(messages.leadingWithCharacterIntro)}
                </p>
                <div className="home-btn">
                  <a
                    className="course-btn gs"
                    href={`/course-overview/${firstOfAllowedCourseId}`}
                  >
                    <span className="btn-d-flex">
                      {intl.formatMessage(messages.getStarted)}
                      <img
                        className="carousel-btn-icon"
                        src={arrowRightImg}
                        alt={intl.formatMessage(messages.getStarted)}
                      />
                    </span>
                  </a>
                </div>
              </div>
              <div className="intro-video">
                <div className="overlap-group-1">
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video
                    className="intro-video-container"
                    width={320}
                    height={240}
                    ref={videoRef}
                    controls
                    style={{ display: isPlaying ? 'block' : 'none' }}
                  >
                    <source
                      src="/video/Leading-With-Character-Introduction.mp4"
                      type="video/mp4"
                    />
                    {intl.formatMessage(messages.videoNotSupported)}
                  </video>
                  {!isPlaying && (
                    <>
                      <img
                        className="prince-akachi-i2ho-d"
                        src={princeAkachiImg}
                        alt="prince-akachi-i2hoD-C2RUA-unsplash_Extended 1"
                      />
                      <button
                        type="button"
                        className="ellipse-21"
                        onClick={handlePlay}
                      >
                        <img
                          className="play"
                          src={playImg}
                          alt="Play"
                        />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="overlap-group4">
          <div className="explore-courses">
            <div className="explore-the-courses">
              {intl.formatMessage(messages.exploreTheCourse)}
            </div>
            <div className="legatum-divider-container">
              <div className="legatum-divider" />
            </div>
            <div className="explore-the-courses" style={{ marginTop: '0px' }}>
              <p className="lorem-ipsum-dolor-si-1 inter-light-congress-blue-22px">
                {intl.formatMessage(messages.characterCourseDescription)}
              </p>
            </div>
            <div className="courses">
              <CarouselCourseCard />
            </div>
          </div>
          <div className="quote">
            <div className="legatum-container display-flex testimonial-custom-gap">
              <div className="quote-text quote-text-custom">
                {intl.formatMessage(messages.quoteText)}
              </div>
              <div className="test-2">
                <img
                  src={pabloPicImg}
                  style={{ borderRadius: '50%' }}
                  alt={intl.formatMessage(messages.quoteAuthor)}
                />
                <div className="simbe-kadarake instrumentserif-normal-congress-blue-48px">
                  -{intl.formatMessage(messages.quoteAuthor)}
                </div>
                <p className="quote-position">
                  {intl.formatMessage(messages.quotePosition)}
                </p>
              </div>
            </div>
          </div>
          <div className="learning-journey">
            <div className="explore-the-courses">
              {intl.formatMessage(messages.learningJourney)}
            </div>
            <div className="legatum-divider-container">
              <div className="legatum-divider" />
            </div>
            <div className="journey-container">
              <div className="w-100">
                <div className="journey-container-text-block">
                  <p className="lorem-ipsum-dolor-si-1 inter-light-congress-blue-22px started">
                    {intl.formatMessage(messages.learningJourneyIntro)}
                  </p>
                  <p className="lorem-ipsum-dolor-si-1 inter-light-congress-blue-22px end">
                    {intl.formatMessage(messages.learningJourneyModules)}
                  </p>
                </div>
                <div className="home-btn">
                  <a
                    className="course-btn gs"
                    href={`/course-overview/${firstOfAllowedCourseId}`}
                  >
                    <span className="btn-d-flex">
                      {intl.formatMessage(messages.getStarted)}
                      <img
                        className="carousel-btn-icon"
                        src={arrowRightImg}
                        alt={intl.formatMessage(messages.getStarted)}
                      />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div style={{ backgroundColor: '#FFF6F6' }}>
            <div className="learning-journey-image homepageContainer">
              <img src={learningJourneyImg} style={{ width: '100%' }} alt="" />
            </div>
          </div>
          {Object.keys(locations).length && (
            <div className="explore-courses">
              <div className="explore-the-courses">
                {intl.formatMessage(messages.globalReach)}
                <p
                  className="lorem-ipsum-dolor-si-1 inter-light-congress-blue-22px"
                  style={{ marginTop: '20px' }}
                >
                  {intl.formatMessage(messages.globalReachDescription)}
                </p>
                <iframe
                  src={mapIframeSrc}
                  ref={mapIframeRef}
                  title={intl.formatMessage(messages.globalReachIframeTitle)}
                  height="100%"
                  width="100%"
                  style={{ height: '100%' }}
                  frameBorder={0}
                />
              </div>
            </div>
          )}
          {footerContent}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
